// GA4 Analytics Helper Functions
// Property: SquareCount - 383709241
// Measurement ID: G-9LZ05NS3YQ

/**
 * Track custom GA4 events
 * @param {string} eventName - The event name
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  // GA4
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
  // PostHog (same event name + props so funnels match across tools)
  if (window.posthog) {
    window.posthog.capture(eventName, params);
  }
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[analytics] ${eventName}`, params);
  }
};

/**
 * Identify the logged-in user so PostHog builds person profiles / funnels and
 * GA4 ties events to a stable user_id. Safe to call repeatedly (idempotent).
 * @param {object} user - user object with user_id, email, name, subscription_tier
 */
export const identifyUser = (user) => {
  if (typeof window === 'undefined' || !user) return;
  const id = user.user_id || user.id;
  if (!id) return;
  if (window.posthog) {
    window.posthog.identify(id, {
      email: user.email,
      name: user.name,
      subscription_tier: user.subscription_tier,
    });
  }
  if (window.gtag) {
    window.gtag('set', { user_id: id });
    window.gtag('set', 'user_properties', { subscription_tier: user.subscription_tier });
  }
};

/**
 * Clear identity on logout so a shared device doesn't merge users.
 */
export const resetUser = () => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.reset();
  }
};

// ==================== AUTH EVENTS ====================

export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method
  });
};

export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method
  });
};

// ==================== ECOMMERCE EVENTS ====================

export const trackBeginCheckout = (item, value, currency = 'USD') => {
  trackEvent('begin_checkout', {
    currency: currency,
    value: value,
    items: [{
      item_id: item.id,
      item_name: item.name,
      price: value
    }]
  });
};

export const trackPurchase = (transactionId, item, value, currency = 'USD') => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency: currency,
    value: value,
    items: [{
      item_id: item.id,
      item_name: item.name,
      price: value
    }]
  });
};

export const trackViewItem = (item, value, currency = 'USD') => {
  trackEvent('view_item', {
    currency: currency,
    value: value,
    items: [{
      item_id: item.id,
      item_name: item.name,
      price: value
    }]
  });
};

// ==================== FEATURE USAGE EVENTS ====================

export const trackMeasurementCreated = (address, roofArea) => {
  trackEvent('measurement_created', {
    address: address,
    roof_area_sqft: roofArea,
    content_type: 'measurement'
  });
};

export const trackReportGenerated = (measurementId, format = 'pdf') => {
  trackEvent('report_generated', {
    measurement_id: measurementId,
    format: format,
    content_type: 'report'
  });
};

export const trackAddressSearch = (address) => {
  trackEvent('search', {
    search_term: address
  });
};

// ==================== CONVERSION EVENTS ====================

export const trackUpgradePromptShown = (currentTier, reason) => {
  trackEvent('upgrade_prompt_shown', {
    current_tier: currentTier,
    reason: reason
  });
};

export const trackUpgradeClicked = (fromTier, toTier) => {
  trackEvent('upgrade_clicked', {
    from_tier: fromTier,
    to_tier: toTier
  });
};

export const trackFreeTierLimitReached = (measurementsUsed) => {
  trackEvent('free_tier_limit_reached', {
    measurements_used: measurementsUsed
  });
};

// ==================== ENGAGEMENT EVENTS ====================

export const trackPageView = (pageName, pageTitle) => {
  trackEvent('page_view', {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: pageTitle || pageName
  });
};

export const trackBlogRead = (postSlug, postTitle) => {
  trackEvent('blog_read', {
    post_slug: postSlug,
    post_title: postTitle,
    content_type: 'blog'
  });
};

export const trackContactFormSubmit = () => {
  trackEvent('generate_lead', {
    source: 'contact_form'
  });
};
